import { Component, Input, OnInit, OnDestroy, ElementRef, ChangeDetectorRef } from '@angular/core';
import { PAGE_FIX_FIXABLE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-fixable-types';
import { PAGE_FIX_CHANGE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-change-types';
import { PAGE_FIX_EVENT } from '@client/app/modules/page-fix/constants/page-fix-event';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { AccessibilityAutofixService } from '@client/app/modules/page-fix/services/accessibility-auto-fix/accessibility-auto-fix.service';
import { TranslateService } from '@client/app/services/translate/translate.service';

interface AttributeData {
    key: string;
    name: string;
    inputPlaceholder: string;
    regexPattern: string;
    regexError: string;
}

interface ExtraSelector {
    type: string;
    value: string;
    newEntry?: boolean;
}

interface ElementAttribute {
    name: string;
    value: string;
}

interface ClassOption {
    name: string;
    value: string;
}

@Component({
    selector: 'page-fix-form-italic-options',
    templateUrl: './page-fix-form-italic-options.component.html',
    standalone: false,
})
export class PageFixFormItalicOptionsComponent implements OnInit, OnDestroy {
    @Input() monPageFix?: any;
    @Input() monPageFixId!: number;
    @Input() monPageFixSelector!: string;
    @Input() monName!: string;
    @Input() monType!: string;
    @Input() monSelected!: boolean;
    @Input() monOrder!: number;
    @Input() monTitle!: string;
    @Input() monFooter!: string;

    classOptions: ClassOption[] = [];
    replacementRegex!: RegExp;
    defaultClassOptionValue: string = 'i:not([class])';
    attributesData: { [key: string]: AttributeData } = {};
    regexes: { [key: string]: RegExp } = {};
    extraSelectors: ExtraSelector[] = [];
    classOptionValue: string = '';
    customElement: string = '';
    replacementSetting: number = 0;
    requestPreparePayloadId: number = 0;
    previewText: string = '';
    elementReplacement: string = 'em';
    elementAttributesClass!: ElementAttribute;
    elementAttributesRole!: ElementAttribute;
    elementAttributesAriaLabel!: ElementAttribute;
    elementAttributes: ElementAttribute[] = [];

    private warningText: string = '';
    private defaultReplacementElement: string = 'em';

    constructor (
        private monEventService: MonEventService,
        private elementRef: ElementRef,
        private accessibilityAutofixService: AccessibilityAutofixService,
        private translateService: TranslateService,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    private activate (): void {
        this.warningText = this.translateService.getString('This option might cause problems to your icons');
        this.defaultReplacementElement = 'em';

        this.replacementRegex = this.accessibilityAutofixService.getElementRegex();
        this.defaultClassOptionValue = 'i:not([class])';
        this.attributesData = {
            class: {
                key: 'class',
                name: 'class',
                inputPlaceholder: this.translateService.getString('Enter class name'),
                regexPattern: '[^\\s]+',
                regexError: this.translateService.getString('Element name should not contain spaces'),
            },
            role: {
                key: 'role',
                name: 'role',
                inputPlaceholder: this.translateService.getString('Enter role name'),
                regexPattern: '[^\\s]+',
                regexError: this.translateService.getString('Element name should not contain spaces'),
            },
            ariaLabel: {
                key: 'ariaLabel',
                name: 'aria-label',
                inputPlaceholder: this.translateService.getString('Enter aria-label name'),
                regexPattern: '',
                regexError: '',
            },
        };

        this.regexes[this.attributesData.class.key] = /\(\.(.*)\)/;
        this.regexes[this.attributesData.role.key] = /\(\[role="(.*)"/;
        this.regexes[this.attributesData.ariaLabel.key] = /\(\[aria-label="(.*)"/;

        this.classOptions = [
            {
                name: this.translateService.getString('Without any assigned class'),
                value: this.defaultClassOptionValue,
            },
            {
                name: this.translateService.getString('All'),
                value: 'i',
            },
        ];

        this.elementAttributesClass = {
            name: this.translateService.getString('Class'),
            value: this.attributesData.class.key,
        };
        this.elementAttributesRole = {
            name: this.translateService.getString('Role'),
            value: this.attributesData.role.key,
        };

        this.elementAttributesAriaLabel = {
            name: this.translateService.getString('Aria-label'),
            value: this.attributesData.ariaLabel.key,
        };

        this.elementAttributes = [this.elementAttributesClass, this.elementAttributesRole, this.elementAttributesAriaLabel];

        this.extraSelectors = [];
        this.classOptionValue = this.defaultClassOptionValue;
        this.customElement = '';
        this.replacementSetting = 0;
        this.requestPreparePayloadId = 0;
        this.previewText = '';

        this.monEventService.run(PAGE_FIX_EVENT.REGISTER_VIEW, this.elementRef.nativeElement, {
            type: this.monType,
            order: this.monOrder,
            footer: this.monFooter,
            title: this.monTitle,
            viewName: this.monName,
            selected: this.monSelected,
        });

        if (this.monPageFix) {
            this.setInitialValues(this.monPageFix);
        }

        this.onChangeSelector();
        this.setupListeners();
        this.updatePreview();
    }

    private onDestroy (): void {
        this.monEventService.run(PAGE_FIX_EVENT.UNREGISTER_VIEW, this.elementRef.nativeElement);
        this.monEventService.remove(this.requestPreparePayloadId);
    }

    addRow (): void {
        this.extraSelectors.push({
            type: this.attributesData.class.key,
            value: '',
            newEntry: true,
        });
        this.cdRef.detectChanges();
    }

    removeRow (index: number): void {
        this.extraSelectors.splice(index, 1);
    }

    getElementText (): string {
        if (this.replacementSetting === 0) {
            return this.elementReplacement;
        }

        return this.customElement;
    }

    updatePreview (): void {
        setTimeout(() => {
            const elementName = this.getElementText();
            const valid = this.accessibilityAutofixService.isElementNameValid(elementName);
            this.monEventService.run(PAGE_FIX_EVENT.CUSTOM_VALIDATION, valid);
            this.setPreview(elementName);
        });
    }

    setReplacementSetting (setting: number): void {
        this.replacementSetting = setting;
        if (setting === 0) {
            this.elementReplacement = this.defaultReplacementElement;
        }
        this.onChangeSelector();
        this.updatePreview();
    }

    trackByIndex (index: number): number {
        return index;
    }

    private onChangeSelector (): void {
        let message = this.warningText;
        if (this.classOptionValue === this.defaultClassOptionValue) {
            message = '';
            this.elementAttributes = [this.elementAttributesRole, this.elementAttributesAriaLabel];
            this.extraSelectors = this.extraSelectors.filter((entry: ExtraSelector) => {
                return entry.type !== this.attributesData.class.key;
            });
        } else {
            this.elementAttributes = [this.elementAttributesClass, this.elementAttributesRole, this.elementAttributesAriaLabel];
        }
        this.monEventService.run(PAGE_FIX_EVENT.UPDATE_WARNING, message);
    }

    private setPreview (elementName: string): void {
        this.previewText = this.accessibilityAutofixService.getElementPreview(elementName);
    }

    private setInitialValues (pageFix: any): void {
        let customSelector: string[];
        let type: string;
        let customSelectorLength = 0;
        let classOptionValue = this.defaultClassOptionValue;

        this.elementReplacement = this.monPageFix.change.value;

        try {
            customSelector = JSON.parse(pageFix.custom_selector)[0].split(/:not[)]*/);
        } catch (_e) {
            return;
        }

        if (Array.isArray(customSelector)) {
            classOptionValue = customSelector.shift() || '';

            if (classOptionValue === 'i') {
                if (customSelector[0] === '([class])') {
                    classOptionValue = 'i:not' + customSelector.shift();
                }

                customSelectorLength = customSelector.length;
                customSelector.forEach((selector: string) => {
                    if (selector.indexOf('[' + this.attributesData.ariaLabel.name) > -1) {
                        type = this.attributesData.ariaLabel.key;
                    } else if (selector.indexOf('[' + this.attributesData.role.name) > -1) {
                        type = this.attributesData.role.key;
                    } else {
                        type = this.attributesData.class.key;
                    }
                    this.extraSelectors.push({
                        type: type,
                        value: (selector.match(this.regexes[type]) || [])[1] || '',
                    });
                });
            } else {
                classOptionValue = this.defaultClassOptionValue;
            }
        }

        this.classOptionValue = classOptionValue;

        if (this.elementReplacement !== this.defaultReplacementElement || customSelectorLength > 0) {
            this.customElement = this.elementReplacement;
            this.replacementSetting = 1;
        }

        if (this.classOptions.find(option => option.value === pageFix.selector)) {
            this.classOptionValue = pageFix.selector;
        }
        this.cdRef.detectChanges();
    }

    private getPayload (): any {
        const payload: any = {
            change: {
                attribute: 'i',
                check_id: this.monPageFixId,
                value: this.getElementText(),
            },
        };

        const extraSelectorsData = [this.classOptionValue];

        if (this.replacementSetting !== 0) {
            let extraSelector: string;
            this.extraSelectors.forEach((item: ExtraSelector) => {
                switch (item.type) {
                    case this.attributesData.class.key:
                        if (this.classOptionValue !== this.defaultClassOptionValue) {
                            extraSelector = item.value ? '.' + item.value : '[class]';
                        }
                        break;
                    case this.attributesData.role.key:
                        extraSelector = item.value ? '[role="' + item.value + '"]' : '[role]';
                        break;
                    case this.attributesData.ariaLabel.key:
                        extraSelector = item.value ? '[aria-label="' + item.value + '"]' : '[aria-label]';
                        break;
                    default:
                        extraSelector = '';
                        break;
                }
                if (extraSelector) {
                    extraSelector = ':not(' + extraSelector + ')';
                }
                if (extraSelector && extraSelectorsData.indexOf(extraSelector) === -1) {
                    extraSelectorsData.push(extraSelector);
                }
            });
        }

        payload.selector = 'q;' + JSON.stringify([extraSelectorsData.join('')]);
        if (!this.monPageFix) {
            payload.fixable_type = PAGE_FIX_FIXABLE_TYPES.accessibility_check;
            payload.fixable_id = this.monPageFixId;
            payload.change_type = PAGE_FIX_CHANGE_TYPES.element + ':i';
        }

        return payload;
    }

    private setupListeners (): void {
        this.requestPreparePayloadId = this.monEventService.addListener(PAGE_FIX_EVENT.REQUEST_PREPARE_PAYLOAD, () => {
            this.monEventService.run(PAGE_FIX_EVENT.REQUEST_SUBMIT, this.getPayload());
        });
    }
}
